window.VAE = window.VAE || {};
VAE.API = (function () {
  'use strict';

  let config = { ...window.VAE.Config };

  function _init(options = {}) {
    if (options) {
      console.log('VAE API override options:', { options });
      Object.assign(config, options);
    }

    console.log('VAE API initialisiert mit:', config);
  }

  /**
   * Removes browser extension elements from HTML string before sending to backend
   * 
   * @param {string} htmlString - Raw HTML string to filter
   * @returns {string} Filtered HTML string with extension elements removed
   */
  function removeExtensionElementsFromString(htmlString) {
    if (typeof htmlString !== 'string' || !htmlString.trim()) {
      return htmlString;
    }

    console.log('VAE.API: Removing extension elements from HTML...');
    
    // Remove custom extension tags and their content
    const extensionTagPatterns = [
      // Custom tags with extension signatures
      /<__[a-z]+__[^>]*data-ext-id="[^"]*"[^>]*>.*?<\/__[a-z]+__>/gs,
      // Common extension custom tags
      /<(ext-|chrome-|moz-|firefox-|edge-|browser-)[^>]*>.*?<\/\1[^>]*>/gs,
      // Generic extension tags
      /<extension-[^>]*>.*?<\/extension-[^>]*>/gs,
    ];
    
    // Apply each pattern to remove matching tags
    for (const pattern of extensionTagPatterns) {
      htmlString = htmlString.replace(pattern, '');
    }
    
    // Clean extension attributes from html tag
    htmlString = htmlString.replace(/<html[^>]*?/i, (match) => {
      // Remove extension-specific attributes from the html tag
      return match
        // Preserve data-vae-id but remove other data-vae-* attributes
        .replace(/\s+ext-[^=]*="[^"]*"/g, '')
        .replace(/\s+chrome-[^=]*="[^"]*"/g, '')
        .replace(/\s+data-ext-[^=]*="[^"]*"/g, '')
        .replace(/\s+class="[^"]*hrp[a-z]+[^"]*"/g, ' class=""');
    });

    // Clean extension attributes from all other elements, preserving data-vae-id
    htmlString = htmlString.replace(/<[a-z][^>]*?>/gi, (match) => {
      return match
        // Preserve data-vae-id but remove other data-vae-* attributes
        .replace(/\s+ext-[^=]*="[^"]*"/g, '')
        .replace(/\s+chrome-[^=]*="[^"]*"/g, '')
        .replace(/\s+data-ext-[^=]*="[^"]*"/g, '');
    });

    console.log('VAE.API: Extension elements removed from HTML');
    return htmlString;
  }

  async function fetchAAIInitialData(options = {}) {
    // Extract onUpdate callback for queue polling
    const { onUpdate: queueUpdateCallback, ...fetchOptions } = options;
    try {
      console.log(
        'VAE.API: fetchAAIInitialData wurde aufgerufen (ohne Kompression)'
      );

      const token = config.client.token;

      if (!token) {
        console.warn(
          'VAE.API: Kein API-Token gefunden. Tracking wird übersprungen.'
        );

        return {
          success: false,
          error: 'No API token found',
          message:
            'Page Impression Tracking übersprungen: Kein Token verfügbar',
        };
      }

      const uidElements = document.querySelectorAll('[id^="uid-"]');
      const allElements = document.querySelectorAll('*');
      const elementsWithoutId = document.querySelectorAll('*:not([id])');

      const idStats = {
        total_elements: allElements.length,
        elements_with_uid: uidElements.length,
        elements_without_id: elementsWithoutId.length,
        uid_coverage_percent: Math.round(
          (uidElements.length / allElements.length) * 100
        ),
      };

      console.log('VAE.API: ID-Statistik vor dem Senden:', idStats);

      const cloneRoot = document.documentElement.cloneNode(true);
      //cloneRoot.querySelectorAll('[id^="vae-"]').forEach(el => el.removeAttribute('id'));
      
      // Filter HTML string to remove browser extension elements
      const filteredHtml = removeExtensionElementsFromString(options.htmlContent || cloneRoot.outerHTML);
      
      const analysisProfile = window.VAE.analysisMode === 'beta' ? 'modern' : 'classic';
      console.log('VAE.API: Analysis-Profil:', analysisProfile);

      // T-006.1.2: Dual-Mode-Support mit automatischem Fallback
      const useConsolidated = options.useConsolidated !== false && config.client.enableConsolidatedAnalysis;
      
      console.log('🔍 VAE.API: Dual-Mode Decision:', {
        useConsolidated,
        explicitlyRequested: options.useConsolidated,
        featureFlagEnabled: config.client.enableConsolidatedAnalysis,
        fallbackEnabled: config.client.consolidatedAnalysisFallback
      });
      
      if (useConsolidated) {
        console.log('🔍 VAE.API: Attempting consolidated analysis first (95% cost reduction)');
        try {
          const consolidatedResult = await fetchConsolidatedAnalysis({
            pageUrl: options.pageUrl || window.location.href,
            htmlContent: filteredHtml,
            analysisProfile,
            axeResults: options.axeResults
          });
          
          console.log('🔍 VAE.API: Consolidated result received:', {
            hasTaskId: !!consolidatedResult.taskId,
            hasIssues: !!consolidatedResult.issues,
            resultKeys: Object.keys(consolidatedResult || {}),
            resultType: typeof consolidatedResult
          });
          
          if (consolidatedResult.taskId || consolidatedResult.issues) {
            console.log('🔍 VAE.API: Consolidated analysis succeeded - 95% cost savings achieved');
            return consolidatedResult;
          } else {
            console.warn('🔍 VAE.API: Consolidated analysis returned unexpected format, falling back to legacy mode:', consolidatedResult);
          }
        } catch (consolidatedError) {
          console.warn('VAE.API: Consolidated analysis failed, falling back to legacy mode:', consolidatedError.message);
          
          // If fallback is disabled, throw the error instead of falling back
          if (!config.client.consolidatedAnalysisFallback) {
            throw consolidatedError;
          }
        }
      } else if (options.useConsolidated === false) {
        // US-001: Check if this is enhanced chunking or legacy mode
        const isEnhancedChunking = options.strategyMetadata && 
                                   options.strategyMetadata.finalStrategy === 'chunking';
        
        if (isEnhancedChunking) {
          console.log('🔍 VAE.API: Using enhanced chunking analysis (US-001)');
          console.log('VAE.API: Enhanced chunking strategy metadata:', {
            strategy: options.strategyMetadata.finalStrategy,
            reason: options.strategyMetadata.strategyReason,
            htmlSizeKB: options.strategyMetadata.htmlSizeKB,
            estimatedTokens: options.strategyMetadata.estimatedTokens
          });
        } else {
          console.log('VAE.API: Consolidated analysis explicitly disabled for this request');
        }
      } else {
        console.log('VAE.API: Consolidated analysis disabled via feature flag');
      }

      // Enhanced chunking or legacy mode
      const isEnhancedMode = options.strategyMetadata && 
                             options.strategyMetadata.finalStrategy === 'chunking';
      console.log(isEnhancedMode ? 
        '🔍 VAE.API: Using enhanced chunking analysis mode' : 
        '🔍 VAE.API: Using legacy analysis mode');
      const data = {
        url: options.pageUrl || window.location.href,
        html: filteredHtml,
        analysisProfile,
        axeResults: options.axeResults || null, // Include axe-core results
        // US-001: Pass strategy metadata to backend
        useConsolidatedAnalysis: options.useConsolidated || false,
        strategyMetadata: options.strategyMetadata || null
      };

      const response = await fetch(config.api.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
        signal: AbortSignal.timeout(60000),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('VAE.API: Antwort-Details:', errorText);
        throw new Error(
          `API-Fehler: ${response.status} ${response.statusText}`
        );
      }

      const result = await response.json();
      
      // NEW: Enhanced Queue-Support with chunk-based processing
      if (response.status === 202 && result.queued) {
        // Detect if this is chunk-based or legacy message-based queue
        const isChunkBased = result.task_id && result.total_chunks;
        
        if (isChunkBased) {
          console.log('VAE.API: Chunk-based analysis queued, starting task polling...', {
            taskId: result.task_id,
            totalChunks: result.total_chunks,
            messageIds: result.message_ids,
            estimatedWaitTime: result.estimated_wait_time
          });

          // NEW: Task-based polling for chunk processing
          return await pollQueueStatus(result.task_id, {
            maxPolls: 150, // Extended timeout for chunk-based processing (5 minutes)
            pollInterval: 2000,
            isTaskId: true, // NEW: Enable chunk-based polling mode
            onUpdate: queueUpdateCallback // Forward callback from core.js
          });
        } else {
          // Legacy message-based polling
          console.log('VAE.API: Legacy analysis queued, starting message polling...', {
            messageId: result.message_id,
            queuePosition: result.queue_position,
            estimatedWaitTime: result.estimated_wait_time
          });

          return await pollQueueStatus(result.message_id, {
            maxPolls: 60,
            pollInterval: 2000,
            isTaskId: false, // Legacy mode
            onUpdate: queueUpdateCallback // Forward callback from core.js
          });
        }
      }

      console.log('VAE.API: Fetching issues erfolgreich:', result);
      return result;

    } catch (error) {
      console.error('VAE.API: Fehler beim Page Impression Tracking:', error);
      return {
        success: false,
        error: error.message,
        message: 'Page Impression Tracking fehlgeschlagen',
      };
    }
  }

  /**
   * Queue-Status-Polling für wartende Analysen
   * Überwacht Queue-Position und geschätzte Wartezeit
   * 
   * @param {number|string} messageId - Queue Message ID oder HTML Hash
   * @param {Object} options - Polling-Optionen
   * @param {number} options.maxPolls - Maximale Anzahl Polls (default: 60)
   * @param {number} options.pollInterval - Poll-Intervall in ms (default: 2000)
   * @param {Function} options.onUpdate - Callback für Status-Updates
   * @returns {Promise<Object>} Finale Analyseergebnisse oder Queue-Status
   */
  /**
   * NEW: Enhanced queue polling with chunk-based support
   * Supports both legacy messageId polling and new taskId-based multi-chunk polling
   */
  async function pollQueueStatus(identifier, options = {}) {
    const {
      maxPolls = 60,
      pollInterval = 2000,
      onUpdate = null,
      isTaskId = false // NEW: Flag to indicate if identifier is taskId or messageId
    } = options;

    const token = config.client.token;
    if (!token) {
      throw new Error('No API token found for queue polling');
    }

    // FIXED: Use correct URL structure with action parameter
    const queueStatusEndpoint = config.api.endpoint;
    
    // NEW: Determine polling mode based on identifier type
    const pollingMode = isTaskId ? 'task-based' : 'message-based';
    console.log(`VAE.API: Starting ${pollingMode} queue polling for ${identifier}`);
    
    for (let poll = 0; poll < maxPolls; poll++) {
      try {
        console.log(`VAE.API: Queue polling attempt ${poll + 1}/${maxPolls} for ${pollingMode} ${identifier}`);
        console.log(`VAE.API: onUpdate callback available:`, typeof onUpdate === 'function');
        
        // FIXED: Build query parameters with action parameter
        const queryParams = isTaskId 
          ? `action=queue-status&taskId=${identifier}` 
          : `action=queue-status&messageId=${identifier}`;
        
        const response = await fetch(`${queueStatusEndpoint}?${queryParams}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          console.error(`VAE.API: Queue status check failed:`, {
            status: response.status,
            statusText: response.statusText,
            url: response.url
          });
          throw new Error(`Queue status check failed: ${response.status}`);
        }

        const status = await response.json();
        console.log(`VAE.API: ${pollingMode} queue status received:`, status);
        console.log(`VAE.API: Raw status object keys:`, Object.keys(status || {}));
        console.log(`VAE.API: Raw status structure:`, {
          status: status?.status,
          queue_position: status?.queue_position,
          estimated_wait_time: status?.estimated_wait_time,
          estimated_completion: status?.estimated_completion,
          chunk_progress: status?.chunk_progress,
          results: status?.results ? 'present' : 'missing',
          error: status?.error
        });

        // NEW: Handle chunk-based progress updates - check if response has chunk data directly
        const hasChunkData = status.total_chunks && status.chunks_completed !== undefined;
        
        if (isTaskId && (status.chunk_progress || hasChunkData)) {
          // Use chunk_progress if available, otherwise build from direct chunk fields
          const chunkData = status.chunk_progress || {
            total_chunks: status.total_chunks,
            chunks_completed: status.chunks_completed,
            chunks_processing: status.chunks_processing || 0,
            chunks_failed: status.chunks_failed || 0,
            chunks_queued: status.chunks_queued || 0
          };
          
          const progress = calculateChunkProgress(chunkData);
          console.log('VAE.API: Chunk progress calculated:', progress);
          
          // Enhanced callback with chunk progress - use new detailed progress display
          if (typeof onUpdate === 'function') {
            console.log('VAE.API: Calling EXTERNAL onUpdate callback with chunk-based data');
            onUpdate({
              ...status,
              chunk_progress: chunkData,
              progress: progress,
              pollingMode: 'chunk-based'
            }, poll + 1);
          } else {
            console.warn('VAE.API: EXTERNAL onUpdate callback not available for chunk-based update');
          }
        } else {
          // Legacy callback for message-based polling
          if (typeof onUpdate === 'function') {
            console.log('VAE.API: Calling EXTERNAL onUpdate callback with message-based data');
            onUpdate({
              ...status,
              pollingMode: 'message-based'
            }, poll + 1);
          } else {
            console.warn('VAE.API: EXTERNAL onUpdate callback not available for message-based update');
          }
        }

        // Analyse abgeschlossen - check multiple completion conditions
        const isCompleted = (status.status === 'completed' && status.results) ||
                           (status.overall_progress >= 100) ||
                           (hasChunkData && status.chunks_completed === status.total_chunks);
                           
        if (isCompleted) {
          console.log(`VAE.API: ${pollingMode} queue analysis completed, returning results`);
          
          // If we have results, return them, otherwise return the status for further processing
          if (status.results) {
            return {
              ...status.results,
              queueMetadata: {
                identifier: identifier,
                pollingMode: pollingMode,
                totalPolls: poll + 1,
                queueTime: status.queue_time || 'unknown',
                // NEW: Chunk-specific metadata
                ...(hasChunkData && {
                  chunkMetadata: {
                    totalChunks: status.total_chunks,
                    completedChunks: status.chunks_completed,
                    failedChunks: status.chunks_failed || 0,
                    finalProgress: status.overall_progress || 100
                  }
                })
              }
            };
          } else {
            // Task completed but results need to be fetched separately
            console.log(`VAE.API: Task completed, but results need separate fetch`);
            return {
              taskCompleted: true,
              taskId: identifier,
              queueMetadata: {
                identifier: identifier,
                pollingMode: pollingMode,
                totalPolls: poll + 1,
                ...(hasChunkData && {
                  chunkMetadata: {
                    totalChunks: status.total_chunks,
                    completedChunks: status.chunks_completed,
                    failedChunks: status.chunks_failed || 0,
                    finalProgress: status.overall_progress || 100
                  }
                })
              }
            };
          }
        }

        // Fehler aufgetreten
        if (status.status === 'failed') {
          // NEW: Enhanced error handling for chunk-based failures
          const errorMessage = isTaskId && status.chunk_progress
            ? `Task analysis failed: ${status.error || 'Unknown error'} (${status.chunk_progress.chunks_failed}/${status.chunk_progress.total_chunks} chunks failed)`
            : `Queue analysis failed: ${status.error || 'Unknown error'}`;
          throw new Error(errorMessage);
        }

        // Cache-Hit - sofortige Rückgabe
        if (status.status === 'cached') {
          console.log(`VAE.API: Cache hit during ${pollingMode} polling, returning cached results`);
          return {
            ...status.results,
            cached: true,
            queueMetadata: {
              identifier: identifier,
              pollingMode: pollingMode,
              totalPolls: poll + 1,
              cacheHit: true
            }
          };
        }

        // Warten vor nächstem Poll
        if (poll < maxPolls - 1) {
          await new Promise(resolve => setTimeout(resolve, pollInterval));
        }

      } catch (error) {
        console.error(`VAE.API: ${pollingMode} queue polling error on attempt ${poll + 1}:`, error);
        
        // Bei letztem Versuch Fehler werfen
        if (poll === maxPolls - 1) {
          throw error;
        }
        
        // Sonst kurz warten und weiter versuchen
        await new Promise(resolve => setTimeout(resolve, pollInterval));
      }
    }

    // Timeout erreicht
    throw new Error(`${pollingMode} queue polling timeout after ${maxPolls} attempts`);
  }

  /**
   * NEW: Calculate chunk-based progress percentage and status
   */
  function calculateChunkProgress(chunkProgress) {
    if (!chunkProgress || !chunkProgress.total_chunks) {
      return { percentage: 0, status: 'unknown' };
    }

    const { total_chunks, chunks_completed, chunks_processing, chunks_failed, chunks_queued } = chunkProgress;
    
    const completedPercentage = Math.round((chunks_completed / total_chunks) * 100);
    const processingPercentage = Math.round((chunks_processing / total_chunks) * 100);
    const failedPercentage = Math.round((chunks_failed / total_chunks) * 100);
    
    let status = 'processing';
    if (chunks_completed === total_chunks) {
      status = 'completed';
    } else if (chunks_failed > 0 && (chunks_completed + chunks_failed) === total_chunks) {
      status = 'completed_with_errors';
    } else if (chunks_processing > 0) {
      status = 'processing';
    } else if (chunks_queued > 0) {
      status = 'queued';
    }

    return {
      percentage: completedPercentage,
      status: status,
      breakdown: {
        completed: completedPercentage,
        processing: processingPercentage,
        failed: failedPercentage,
        queued: Math.round((chunks_queued / total_chunks) * 100)
      },
      chunks: {
        total: total_chunks,
        completed: chunks_completed,
        processing: chunks_processing,
        failed: chunks_failed,
        queued: chunks_queued
      }
    };
  }

  /**
   * T-006.1.1: Neue konsolidierte WCAG-Analyse-Funktion
   * Führt eine cost-effective Analyse durch (95% Kosteneinsparung)
   * Verwendet den Task Manager mit konsolidierter Analyse-Flag
   * 
   * @param {Object} options - Analyse-Optionen
   * @param {string} options.pageUrl - URL der zu analysierenden Seite
   * @param {string} options.htmlContent - HTML-Inhalt der Seite
   * @param {string} options.analysisProfile - Analyse-Profil ('classic'|'modern')
   * @param {Object} options.axeResults - Axe-Core-Analyseergebnisse
   * @returns {Promise<Object>} Konsolidierte Analyseergebnisse
   */
  async function fetchConsolidatedAnalysis(options = {}) {
    try {
      console.log('VAE.API: fetchConsolidatedAnalysis gestartet');
      
      const token = config.client.token;
      if (!token) {
        throw new Error('No API token found for consolidated analysis');
      }

      // Use the normal Task Manager endpoint with consolidated flag
      const endpoint = config.api.endpoint;
      console.log('VAE.API: Using Task Manager endpoint with consolidated flag:', endpoint);

      const data = {
        url: options.pageUrl || window.location.href,
        html: options.htmlContent,
        analysisProfile: options.analysisProfile || 'classic',
        axeResults: options.axeResults || null,
        // NEW: Flag für konsolidierte Analyse im Task Manager
        useConsolidatedAnalysis: true,
        consolidatedAnalysisMode: 'full-wcag-consolidation' // Specific mode identifier
      };

      console.log('VAE.API: Sending consolidated analysis request through Task Manager...');
      const startTime = performance.now();

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
        signal: AbortSignal.timeout(90000), // Extended timeout for consolidated analysis
      });

      const endTime = performance.now();
      const duration = Math.round(endTime - startTime);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('VAE.API: Consolidated analysis error:', errorText);
        throw new Error(
          `Consolidated API error: ${response.status} ${response.statusText}`
        );
      }

      const result = await response.json();
      
      // NEW: Enhanced Queue-Support für konsolidierte Analyse mit Chunk-Detection
      if (response.status === 202 && result.queued) {
        // Detect if this is chunk-based or legacy consolidated analysis
        const isChunkBased = result.task_id && result.total_chunks;
        
        if (isChunkBased) {
          console.log('VAE.API: Chunk-based consolidated analysis queued, starting task polling...', {
            taskId: result.task_id,
            totalChunks: result.total_chunks,
            messageIds: result.message_ids,
            estimatedWaitTime: result.estimated_wait_time,
            analysisMode: 'consolidated-chunked'
          });

          // NEW: Task-based polling for chunk-based consolidated analysis
          const queueResult = await pollQueueStatus(result.task_id, {
            maxPolls: 180, // Extended timeout for consolidated chunk processing (6 minutes)
            pollInterval: 2000,
            isTaskId: true, // Enable chunk-based polling mode
            onUpdate: (status, pollCount) => {
              console.log(`VAE.API: Consolidated chunk-based queue update ${pollCount}:`, {
                status: status.status,
                progress: status.progress,
                chunks: status.chunk_progress
              });
              
              // Enhanced UI updates with chunk progress for consolidated analysis
              if (window.VAE && window.VAE.events) {
                window.VAE.events.trigger('consolidated-chunk-queue-update', {
                  status: status.status,
                  progress: status.progress,
                  chunkProgress: status.chunk_progress,
                  pollCount,
                  analysisType: 'consolidated-chunked'
                });
              }
            }
          });
          
          // Add enhanced consolidation metadata for chunk-based processing
          if (queueResult) {
            queueResult.consolidationMetadata = {
              analysisMode: 'consolidated-chunked',
              duration: Date.now() - startTime,
              costSavings: '~95%',
              multiCriterionSupport: true,
              axeCoreIntegration: !!options.axeResults,
              routedThroughOrchestrator: true,
              pageIdGenerated: true,
              queueProcessed: true,
              chunkBasedProcessing: true,
              totalChunks: result.total_chunks,
              chunkMetadata: queueResult.queueMetadata?.chunkMetadata
            };
          }
          
          return queueResult;
        } else {
          // Legacy message-based consolidated analysis
          console.log('VAE.API: Legacy consolidated analysis queued, starting message polling...', {
            messageId: result.message_id,
            queuePosition: result.queue_position,
            estimatedWaitTime: result.estimated_wait_time,
            analysisMode: 'consolidated-legacy'
          });

          const queueResult = await pollQueueStatus(result.message_id, {
            maxPolls: 90, // Standard timeout for legacy consolidated analysis
            pollInterval: 2000,
            isTaskId: false, // Legacy mode
            onUpdate: (status, pollCount) => {
              console.log(`VAE.API: Legacy consolidated queue update ${pollCount}:`, status);
              
              // Legacy UI updates for consolidated analysis using new detailed progress display
              if (window.VAE && window.VAE.AnalysisDisplay && window.VAE.AnalysisDisplay.showQueueProgress) {
                window.VAE.AnalysisDisplay.showQueueProgress({
                  ...status,
                  pollingMode: 'legacy-consolidated',
                  analysisType: 'consolidated-legacy'
                });
              }
              
              // Keep legacy event system for backwards compatibility
              if (window.VAE && window.VAE.events) {
                window.VAE.events.trigger('consolidated-queue-update', {
                  status: status.status,
                  position: status.queue_position,
                  waitTime: status.estimated_wait_time,
                  pollCount,
                  analysisType: 'consolidated-legacy'
                });
              }
            }
          });
          
          // Add legacy consolidation metadata
          if (queueResult) {
            queueResult.consolidationMetadata = {
              analysisMode: 'consolidated-legacy',
              duration: Date.now() - startTime,
              costSavings: '~95%',
              multiCriterionSupport: true,
              axeCoreIntegration: !!options.axeResults,
              routedThroughOrchestrator: true,
              pageIdGenerated: true,
              queueProcessed: true,
              chunkBasedProcessing: false
            };
          }
          
          return queueResult;
        }
      }
      
      // T-006.1.4: Cost-Tracking und Performance-Metriken
      console.log(`VAE.API: Consolidated analysis completed in ${duration}ms`);
      console.log('VAE.API: Estimated cost savings: ~95% vs legacy mode');
      
      // Enhanced result with consolidation metadata
      result.consolidationMetadata = {
        analysisMode: 'consolidated',
        duration,
        costSavings: '~95%',
        multiCriterionSupport: true,
        axeCoreIntegration: !!options.axeResults,
        routedThroughOrchestrator: true,
        pageIdGenerated: true
      };

      console.log('VAE.API: Consolidated analysis successful with page ID:', result);
      return result;

    } catch (error) {
      console.error('VAE.API: Fehler bei konsolidierter Analyse:', error);
      throw error; // Re-throw for fallback handling
    }
  }

  function subscribeToSSEUpdates(taskId, options = {}) {
    const token = config.client.token;

    if (!token) {
      console.warn('VAE.API: Kein API-Token gefunden. SSE-Verbindung nicht möglich.');
      return { 
        close: () => {},
        error: 'No API token found' 
      };
    }

    if (!taskId) {
      console.warn('VAE.API: Kein Task-ID angegeben. SSE-Verbindung nicht möglich.');
      return { 
        close: () => {},
        error: 'No task ID provided' 
      };
    }

    const sseEndpoint = `${config.api.endpoint}/${taskId}/events`;
    
    console.log(`VAE.API: Starte SSE-Verbindung zu ${sseEndpoint}`);
    
    let eventSource;

    // 🔧 CORS-FIX für localhost testing: Keine withCredentials verwenden
    const headers = {
      // withCredentials: true, // ❌ DEAKTIVIERT - verursacht CORS-Fehler bei localhost
      headers: {
        'Authorization': `Bearer ${token}`
      }
    };
    
    try {
      if (window.EventSourcePolyfill) {
        console.log('VAE.API: Benutze polyfill')
        eventSource = new window.EventSourcePolyfill(sseEndpoint, headers);
      } else {
        const url = new URL(sseEndpoint);
        url.searchParams.append('token', token);
        // 🔧 CORS-FIX: Keine withCredentials für localhost
        eventSource = new EventSource(url.toString()); // {withCredentials: true} entfernt
        console.log('VAE.API: Standard EventSource für localhost testing (ohne credentials)');
      }
      
      eventSource.onopen = function(event) {
        console.log('VAE.API: SSE-Verbindung geöffnet');
        if (typeof options.onOpen === 'function') {
          options.onOpen(event);
        }
      };
      
      eventSource.onmessage = function(event) {
        try {
          const data = JSON.parse(event.data);
          console.log('VAE.API: SSE-Nachricht empfangen:', data);
          if (typeof options.onMessage === 'function') {
            options.onMessage(data, event);
          }
        } catch (error) {
          console.error('VAE.API: Fehler beim Parsen der SSE-Nachricht:', error, event.data);
          if (typeof options.onError === 'function') {
            options.onError(error, event);
          }
        }
      };
      
      eventSource.onerror = function(error) {
        console.error('VAE.API: SSE-Verbindungsfehler:', error);
        if (typeof options.onError === 'function') {
          options.onError(error);
        }
      };
      
      if (options.events && typeof options.events === 'object') {
        Object.entries(options.events).forEach(([eventName, callback]) => {
          if (typeof callback === 'function') {
            console.log('VAE.API: Registriere benutzerdefinierte Events:', eventName);
            eventSource.addEventListener(eventName, function(event) {
              try {
                const data = JSON.parse(event.data);
                callback(data, event);
              } catch (error) {
                console.error(`VAE.API: Fehler beim Parsen der ${eventName}-Nachricht:`, error);
              }
            });
          }
        });
      }
      
      return {
        eventSource,
        close: function() {
          console.log('VAE.API: Schließe SSE-Verbindung');
          eventSource.close();
        }
      };
    } catch (error) {
      console.error('VAE.API: Fehler beim Erstellen der SSE-Verbindung:', error);
      if (typeof options.onError === 'function') {
        options.onError(error);
      }
      return { 
        close: () => {},
        error: error.message 
      };
    }
  }

  async function fetchBatchAnalysis() {
    console.log('VAE.API.fetchBatchAnalysis: Fetching batch analysis');
    if (!VAE.DOM.pendingSet.size) return;
    const fragments = Array.from(VAE.DOM.pendingSet).map(id => {
      const el = document.getElementById(id);
      console.log('VAE.API.fetchBatchAnalysis: Found element:', el);
      if (!el) return '';
      const clone = el.cloneNode(true);
      if (clone.id && clone.id.startsWith('vae-')) clone.removeAttribute('id');
      clone.querySelectorAll('[id^="vae-"]').forEach(child => child.removeAttribute('id'));
      return clone.outerHTML;
    }).join('');
    const batchHtml = `<div id=\"vae-batch-root\">${fragments}</div>`;
    VAE.DOM.pendingSet.clear();
    const token = config.client.token;
    
    // Filter HTML string to remove browser extension elements
    const filteredBatchHtml = removeExtensionElementsFromString(batchHtml);
    
    const response = await fetch(config.api.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ 
        url: window.location.href, 
        html: filteredBatchHtml,
        analysisProfile: 'modern'
      }),
      signal: AbortSignal.timeout(config.api.timeout)
    });
    if (!response.ok) {
      throw new Error(`VAE.API: Batch analysis failed: ${response.status}`);
    }
    const result = await response.json();
    console.log('VAE.API: fetchBatchAnalysis erfolgreich:', result);
    return result;  
  }

  async function fetchTaskResults(taskId) {
    console.log(`VAE.API: Fetching complete task results for task ${taskId}`);
    const token = config.client.token;

    // Add token debug info (first 10 chars only for security)
    console.log(`VAE.API: Using token (first 10 chars): ${token ? token.substring(0, 10) + '...' : 'undefined'}`);

    if (!token) {
      console.warn('VAE.API: Kein API-Token gefunden. Abruf der Aufgabenergebnisse nicht möglich.');
      throw new Error('No API token found');
    }

    if (!taskId) {
      console.warn('VAE.API: Keine Task-ID angegeben. Abruf der Aufgabenergebnisse nicht möglich.');
      throw new Error('No task ID provided');
    }

    const resultsEndpoint = `${config.api.endpoint}/${taskId}/results`;
    
    try {
      console.log(`VAE.API: Rufe Endergebnisse von ${resultsEndpoint} ab`);
      
      const response = await fetch(resultsEndpoint, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        signal: AbortSignal.timeout(config.api.timeout || 30000) // Default 30s timeout
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('VAE.API: Fehler beim Abrufen der Ergebnisse:', errorText);
        console.error('VAE.API: Response status:', response.status, response.statusText);
        console.error('VAE.API: Response headers:', JSON.stringify(Object.fromEntries([...response.headers.entries()])));
        throw new Error(`Fehler beim Abrufen der Ergebnisse: ${response.status} ${response.statusText}`);
      }

      const results = await response.json();
      console.log('VAE.API: Ergebnisse erfolgreich abgerufen:', results);
      return results;
    } catch (error) {
      console.error('VAE.API: Fehler beim Abrufen der Aufgabenergebnisse:', error);
      console.error('VAE.API: Error details:', error.name, error.message, error.stack);
      throw error;
    }
  }

  async function fetchSubtaskResults(subtaskId) {
    console.log(`VAE.API: Fetching complete results for subtask ${subtaskId}`);
    const token = config.client.token;

    // Add token debug info (first 10 chars only for security)
    console.log(`VAE.API: Using token (first 10 chars): ${token ? token.substring(0, 10) + '...' : 'undefined'}`);

    if (!token) {
      console.warn('VAE.API: No API token found. Cannot fetch subtask results.');
      throw new Error('No API token found');
    }

    if (!subtaskId) {
      console.warn('VAE.API: No subtask ID provided. Cannot fetch subtask results.');
      throw new Error('No subtask ID provided');
    }

    const subtaskResultsEndpoint = `${config.api.endpoint}/subtasks/${subtaskId}/results`;
    
    try {
      console.log(`VAE.API: Fetching subtask results from ${subtaskResultsEndpoint}`);
      
      const response = await fetch(subtaskResultsEndpoint, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        signal: AbortSignal.timeout(config.api.timeout || 30000) // Default 30s timeout
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('VAE.API: Error fetching subtask results:', errorText);
        console.error('VAE.API: Response status:', response.status, response.statusText);
        console.error('VAE.API: Response headers:', JSON.stringify(Object.fromEntries([...response.headers.entries()])));
        throw new Error(`Error fetching subtask results: ${response.status} ${response.statusText}`);
      }

      const results = await response.json();
      console.log('VAE.API: Subtask results fetched successfully:', results);
      return results;
    } catch (error) {
      console.error('VAE.API: Error fetching subtask results:', error);
      console.error('VAE.API: Error details:', error.name, error.message, error.stack);
      throw error;
    }
  }

  async function fetchPageResults(pageId) {
    console.log(`VAE.API: Fetching analysis results for page ${pageId} from pages table`);
    const token = config.client.token;

    console.log(`VAE.API: Using token (first 10 chars): ${token ? token.substring(0, 10) + '...' : 'undefined'}`);

    if (!token) {
      console.warn('VAE.API: No API token found. Cannot fetch page results.');
      throw new Error('No API token found for fetchPageResults');
    }

    if (!pageId) {
      console.warn('VAE.API: No page ID provided. Cannot fetch page results.');
      throw new Error('No page ID provided for fetchPageResults');
    }

    const pageResultsEndpoint = `${config.api.endpoint}/page-results/${pageId}`;
    
    try {
      console.log(`VAE.API: Fetching page results from ${pageResultsEndpoint}`);
      
      const response = await fetch(pageResultsEndpoint, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        signal: AbortSignal.timeout(config.api.timeout || 30000) // Default 30s timeout
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('VAE.API: Error fetching page results:', errorText);
        console.error('VAE.API: Response status:', response.status, response.statusText);
        console.error('VAE.API: Response headers:', JSON.stringify(Object.fromEntries([...response.headers.entries()])));
        throw new Error(`Error fetching page results: ${response.status} ${response.statusText}`);
      }

      const results = await response.json();
      console.log('VAE.API: Page results fetched successfully:', results);
      return results;
    } catch (error) {
      console.error('VAE.API: Error in fetchPageResults:', error);
      console.error('VAE.API: Error details:', error.name, error.message, error.stack);
      throw error;
    }
  }

  /**
   * Queue-Statistiken abrufen für Admin-Dashboard
   * 
   * @returns {Promise<Object>} Queue-Metriken und Statistiken
   */
  async function fetchQueueStats() {
    try {
      const token = config.client.token;
      if (!token) {
        throw new Error('No API token found for queue stats');
      }

      // FIXED: Use correct URL structure with action parameter
      const queueStatsEndpoint = `${config.api.endpoint}?action=queue-stats`;
      
      const response = await fetch(queueStatsEndpoint, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Queue stats fetch failed: ${response.status}`);
      }

      const stats = await response.json();
      console.log('VAE.API: Queue stats received:', stats);
      
      return stats;

    } catch (error) {
      console.error('VAE.API: Error fetching queue stats:', error);
      throw error;
    }
  }

  async function submitProblemReport(reportData) {
    const token = config.client.token;
    const reportEndpoint = VAE.Config.api.reportProblemEndpoint;

    if (!reportEndpoint) {
      console.error('VAE.API: reportProblemEndpoint is not defined in VAE.Config.api.');
      return Promise.reject(new Error('Report endpoint configuration missing.'));
    }

    if (!token) {
      console.error('VAE.API: API Token not available. Cannot submit report.');
      return Promise.reject(new Error('API Token not available.')); 
    }

    if (!reportData || typeof reportData !== 'object') {
      console.error('VAE.API: Invalid reportData provided.');
      return Promise.reject(new Error('Invalid report data provided.'));
    }

    console.log(`VAE.API: Submitting problem report to ${reportEndpoint}`);

    try {
      const response = await fetch(reportEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(reportData),
      });

      return response; 

    } catch (error) {
      console.error('VAE.API: Network error during problem report submission:', error);
      throw error; 
    }
  }

  _init();

  return {
    fetchAAIInitialData,
    fetchConsolidatedAnalysis,
    subscribeToSSEUpdates,
    fetchBatchAnalysis,
    submitProblemReport,
    fetchTaskResults,
    fetchSubtaskResults,
    fetchPageResults,
    // Queue-Support Functions
    pollQueueStatus,
    fetchQueueStats,
    // NEW: Chunk-based processing utilities
    calculateChunkProgress
  };
})();

console.log('VAE: API-Modul initialisiert');
